import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbDeviceDesktop, TbMoon, TbSun } from "solid-icons/tb";
import { type Component, Match, Switch } from "solid-js";
import { useThemePreference } from "~/hooks/useThemePreference";
import { useI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import type { ThemePreference } from "~/utils/themeCookie";

const NEXT_PREFERENCE: Record<ThemePreference, ThemePreference> = {
  light: "dark",
  dark: "system",
  system: "light",
};

/**
 * Cycles light → dark → follow-the-OS. The icon is the only indicator of which
 * of the three is active, so the label names the current one rather than the
 * action.
 */
export const ThemeToggle: Component<{ placement: "top" | "right" }> = (props) => {
  const { t } = useI18n();
  const { themePreference, setThemePreference } = useThemePreference();

  const label = () => {
    switch (themePreference()) {
      case "light":
        return t("nav.theme_light");
      case "dark":
        return t("nav.theme_dark");
      default:
        return t("nav.theme_system");
    }
  };

  return (
    <StTooltip content={label()} placement={props.placement}>
      <StTooltipTrigger
        as="button"
        type="button"
        aria-label={label()}
        class="flex cursor-pointer items-center justify-center rounded-md p-2 transition-colors hover:bg-canvas"
        onClick={() => {
          trackFeature({ feature: "nav.theme", via: "button" });
          setThemePreference(NEXT_PREFERENCE[themePreference()]);
        }}
      >
        <Switch>
          <Match when={themePreference() === "light"}>
            <TbSun class="size-5 text-secondary-text" aria-hidden />
          </Match>
          <Match when={themePreference() === "dark"}>
            <TbMoon class="size-5 text-secondary-text" aria-hidden />
          </Match>
          <Match when={themePreference() === "system"}>
            <TbDeviceDesktop class="size-5 text-secondary-text" aria-hidden />
          </Match>
        </Switch>
      </StTooltipTrigger>
    </StTooltip>
  );
};
